import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { InfoBox } from '@tryhackme/thm-ui-components';
import styled from 'styled-components';

export const StyledTableWrapper = styled.div<{ $isModuleScoreboard: boolean }>`
  div[role='row'] {
    padding: 0;
    min-height: 6.4rem;
    ${({ $isModuleScoreboard }) => ($isModuleScoreboard ? 'display: grid;' : '')}

    div[role='columnheader'] {
      align-self: center;
      border: ${({ theme }) => `1px solid ${theme.colors.border.main}`};
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      :nth-child(2) {
        justify-content: flex-start;
        padding: 0 1.3rem;
      }
    }

    ${({ $isModuleScoreboard, theme }) =>
      $isModuleScoreboard &&
      `div[role='columnheader']:last-child {
        position: sticky;
        right: 0;
        background-color: ${theme.colors.background.tableEven};
      }`}
  }

  div[role='table'] {
    min-width: ${({ theme }) => `${theme.sizes.lg}`};
    overflow-x: auto;
    position: relative;

    @media (max-width: ${({ theme }) => theme.sizes.md}) {
      overflow-x: initial;
    }
  }

  ${({ $isModuleScoreboard }) =>
    $isModuleScoreboard &&
    `div[role='rowgroup'] {
      div[role='row'] {
        .score:last-child {
          position: sticky;
          right: 0;
        }
      }
    }
  `}

  @media screen and (max-width: ${({ theme }) => theme.sizes.lg}) {
    overflow-x: scroll;
  }
`;

export const StyledRow = styled.div`
  :nth-child(even) {
    & > div {
      background-color: ${({ theme }) => theme.colors.background.tableEven};
    }
  }
  :nth-child(odd) {
    & > div {
      background-color: ${({ theme }) => theme.colors.background.tableOdd};
    }
  }
`;

export const TeamStyledRow = styled(StyledRow)<{ isOpen: boolean }>`
  display: ${({ isOpen }) => !isOpen && 'none ! important'};

  padding-left: 2rem;
  font-size: ${({ theme }) => theme.fonts.sizes.small};
  :nth-child(even) {
    & > div {
      background-color: ${({ theme }) => theme.colors.background.tableEven};
    }
  }
  :nth-child(odd) {
    & > div {
      background-color: ${({ theme }) => theme.colors.background.tableOdd};
    }
  }
`;

export const StyledCell = styled.div<{ $isCurrentTeam?: boolean }>`
  text-align: center;
  font-weight: ${({ theme, $isCurrentTeam }) =>
    $isCurrentTeam ? `${theme.fonts.weight.bolder}` : `${theme.fonts.weight.regular}`};
  height: 100%;
  border: ${({ theme }) => `1px solid ${theme.colors.border.main}`};
  width: 100%;
  padding: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  :first-child {
    border-left: ${({ theme }) => `1px solid ${theme.colors.border.main}}`};
    border-right: ${({ theme }) => `1px solid ${theme.colors.border.main}}`};
  }
`;

export const SubTableStyledCell = styled.div`
  text-align: center;
  height: 100%;
  border: ${({ theme }) => `1px solid ${theme.colors.border.main}`};
  width: 100%;

  padding-left: 5rem;

  display: flex;
  align-items: center;
  justify-content: center;
  border-left: ${({ theme }) => `1px solid ${theme.colors.border.main}}`};
  position: relative; /* Important for positioning the pseudo-element */

  &::before {
    /* Target the ::before pseudo-element */
    content: '';
    position: absolute;
    top: 0;
    left: 2.25rem; /* Position at the very left edge */
    height: 100%;
    width: 2px; /* Adjust thickness as needed */
    background-color: ${({ theme }) => theme.colors.border.main}; /* Use your theme color */
  }
`;

export const StyledCheckIcon = styled(FontAwesomeIcon)`
  color: ${({ theme }) => `${theme.colors.checkIcon.main}`};
`;
export const StyledAccordionIcon = styled(FontAwesomeIcon)`
  padding: 0 0.8rem;
  color: ${({ theme }) => `${theme.colors.grey[600]}`};
`;

export const StyledDisabledCheckIcon = styled(FontAwesomeIcon)`
  color: ${({ theme }) => `${theme.colors.checkIcon.disabled}`};
  padding: 0.8rem;
`;

export const StyledUsername = styled.a`
  font-size: 1.8rem;
  font-weight: ${({ theme }) => `${theme.fonts.weight.regular}`};
  width: 100%;
  text-align: left;
  text-decoration: none;
  display: block;
  &:hover {
    text-decoration: underline;
  }
`;

export const StyledTeamname = styled.span<{ $isCurrentTeam: boolean }>`
  font-size: 1.8rem;
  font-weight: ${({ theme, $isCurrentTeam }) =>
    $isCurrentTeam ? `${theme.fonts.weight.bolder}` : `${theme.fonts.weight.regular}`};
  width: 100%;
  text-align: left;
  text-decoration: none;
  display: block;
`;

export const StyledUsernameWrapper = styled.div`
  display: block;
`;

export const StyledUsernameSecondRowWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

export const StyledCheckIconWrapper = styled.span`
  position: relative;

  & > div:first-of-type {
    position: absolute;
    top: -5.4rem;
    left: -3.5rem;
    position: absolute;
    width: 8.5rem;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.3s ease, visibility 0.3s ease;
    font-size: ${({ theme }) => `${theme.fonts.sizes.small}`};
    font-weight: ${({ theme }) => `${theme.fonts.weight.regular}`};
  }
  &:hover {
    & > div:first-of-type {
      visibility: visible;
      opacity: 1;
    }
  }
`;

export const StyledInfoBox = styled(InfoBox)`
  font-size: ${({ theme }) => `${theme.fonts.sizes.default}`};
`;

export const StyledRecordDetailText = styled.span`
  font-size: ${({ theme }) => theme.fonts.sizes.default};
  font-family: ${({ theme }) => theme.fonts.familySansPro};
  color: ${({ theme }) => theme.colors.text.lighter};
`;

export const StyledPaginationContainer = styled.div`
  margin-top: 3rem;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  @media screen and (max-width: ${({ theme }) => theme.sizes.md}) {
    justify-content: center;
    gap: 2.4rem;
  }
`;
